/**
 * "Hazard packs" (help `cloud.hazard-packs`; Diurnal parity 06-034, the tool's "Manage Oz Runways Feeds"; decisions
 * 88, 625, 808, 809; docs/specs/cloud/hazard-packs.md): each project's pilot hazard pack as Teslyn Field published it, what it
 * holds, its link and how long it works, with "View", "Copy link", "Withdraw link" and "New link". Every staff role
 * with access to the project; clients never (the database gives them no pack).
 */
import { useCallback, useEffect, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  hazardPackContents,
  hazardPackDateTime,
  hazardPackExpiryWords,
  hazardPackLandingUrl,
  type HazardPackLinkState,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { ErrorState, Loading, PageHeader } from '../components/States.tsx';
import { loadHazardPacks, newPackLink, revokePackLink, type PackLinkRow, type PackRow } from './api.ts';
import { TableWrap } from '../components/TableWrap.tsx';

const message = (e: unknown): string => (e instanceof Error ? e.message : String(e));

const STATE: Readonly<Record<HazardPackLinkState, { word: string; chip: string }>> = {
  live: { word: 'Works', chip: 'chip chip--pass' },
  revoked: { word: 'Withdrawn', chip: 'chip chip--fail' },
  expired: { word: 'Expired', chip: 'chip chip--warn' },
};

/** The link the row is about: the working one, else the newest. */
export const currentLink = (p: PackRow): PackLinkRow | null =>
  p.links.find((l) => l.state === 'live') ?? p.links[0] ?? null;

export interface HazardPacksScreenProps {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
  readonly tenantName: string;
  /** Teslyn Cloud's address for the landing pages (this site). */
  readonly origin?: string;
  readonly now?: () => number;
}

export function HazardPacksScreen({
  sb,
  tenantId,
  tenantName,
  origin = window.location.origin,
  now = Date.now,
}: HazardPacksScreenProps): ReactElement {
  const [rows, setRows] = useState<PackRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const reload = useCallback(() => {
    setError(null);
    loadHazardPacks(sb, tenantId)
      .then(setRows)
      .catch((e: unknown) => setError(message(e)));
  }, [sb, tenantId]);
  useEffect(reload, [reload]);

  async function withdraw(p: PackRow, l: PackLinkRow): Promise<void> {
    setBusy(l.id);
    setNotice(null);
    try {
      await revokePackLink(sb, l.id);
      setNotice({
        ok: true,
        text: `The link of ${p.project_number} was withdrawn: it no longer works. Make a new link and send it to the pilots who still need the pack.`,
      });
      reload();
    } catch (e) {
      setNotice({ ok: false, text: `The link was not withdrawn: ${message(e)}` });
    } finally {
      setBusy(null);
      setConfirming(null);
    }
  }

  async function makeLink(p: PackRow): Promise<void> {
    setBusy(p.id);
    setNotice(null);
    try {
      await newPackLink(sb, p.id);
      setNotice({
        ok: true,
        text: `A new link for ${p.project_number} was made: copy it and send it to the pilots.`,
      });
      reload();
    } catch (e) {
      setNotice({ ok: false, text: `No new link was made: ${message(e)}` });
    } finally {
      setBusy(null);
    }
  }

  async function copy(p: PackRow, url: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(url);
      setNotice({ ok: true, text: `Copied the link of ${p.project_number}.` });
    } catch {
      setNotice({ ok: false, text: `The link could not be copied: ${url}` });
    }
  }

  return (
    <section className="page page--wide">
      <PageHeader
        title="Hazard packs"
        subtitle={`Each project's hazards for pilots in Oz Runways, as ${tenantName} staff published them from Teslyn Field.`}
      />
      <p className="body">
        Pilots open a project&apos;s link on their iPad, download the pack and load it in Oz Runways. Oz
        Runways does not update it by itself: when the pack is published again, the page shows the new version
        and pilots download it again. A link works until 30 days after its project ends. Withdraw a link that
        went to someone who should no longer have it: it stops at once, and a new link can be made for the
        others.
      </p>
      {notice && (
        <p className={notice.ok ? 'notice' : 'notice notice--fail'} role={notice.ok ? 'status' : 'alert'}>
          {notice.text}
        </p>
      )}
      {error && <ErrorState message={error} onRetry={reload} />}
      {!rows && !error && <Loading what="hazard packs" />}
      {rows && (
        <TableWrap>
          <table className="table">
            <thead>
              <tr>
                <th className="label">Project</th>
                <th className="label">Pack</th>
                <th className="label">Published</th>
                <th className="label">What it holds</th>
                <th className="label">Link</th>
                <th className="label">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="muted">
                    No hazard pack yet. Publish one from Teslyn Field: Map, Files, &quot;Publish for
                    pilots…&quot;.
                  </td>
                </tr>
              )}
              {rows.map((p) => {
                const link = currentLink(p);
                const url = link ? hazardPackLandingUrl(origin, link.token) : null;
                const earlier = p.links.filter((l) => l !== link).length;
                return (
                  <tr key={p.id}>
                    <td className="body">
                      <span className="data">{p.project_number}</span> {p.project_title}
                    </td>
                    <td className="body">
                      {p.name}
                      <div className="caption muted">
                        Version {p.version}, {(p.size_bytes / 1024 / 1024).toFixed(1)} MB
                      </div>
                    </td>
                    <td className="caption">
                      {hazardPackDateTime(p.published_at)}
                      {p.published_by_name && <div className="muted">by {p.published_by_name}</div>}
                    </td>
                    <td className="caption">{hazardPackContents(p.stats).join(', ') || 'Nothing counted'}</td>
                    <td className="caption">
                      {link ? (
                        <>
                          <span className={STATE[link.state].chip}>{STATE[link.state].word}</span>{' '}
                          {hazardPackExpiryWords(link, now())}
                          <div className="muted">
                            {link.last_fetched_at
                              ? `Last fetched ${hazardPackDateTime(link.last_fetched_at)} (${link.fetch_count} time${link.fetch_count === 1 ? '' : 's'})`
                              : 'Not fetched yet'}
                          </div>
                          {earlier > 0 && (
                            <div className="muted">
                              {earlier} earlier link{earlier === 1 ? '' : 's'} no longer work
                              {earlier === 1 ? 's' : ''}.
                            </div>
                          )}
                        </>
                      ) : (
                        'No link'
                      )}
                    </td>
                    <td className="table__actions">
                      {link?.state === 'live' && url && confirming === link.id ? (
                        <>
                          <span className="caption">Pilots who added this link stop getting the pack.</span>
                          <Button
                            variant="primary"
                            busy={busy === link.id}
                            onClick={() => void withdraw(p, link)}
                          >
                            Withdraw
                          </Button>
                          <Button variant="quiet" onClick={() => setConfirming(null)}>
                            Keep
                          </Button>
                        </>
                      ) : link?.state === 'live' && url ? (
                        <>
                          <a
                            className="btn btn--quiet"
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={`View the page of ${p.project_number}`}
                          >
                            View
                          </a>
                          <Button
                            variant="quiet"
                            aria-label={`Copy the link of ${p.project_number}`}
                            onClick={() => void copy(p, url)}
                          >
                            Copy link
                          </Button>
                          <Button
                            variant="quiet"
                            aria-label={`Withdraw the link of ${p.project_number}`}
                            onClick={() => setConfirming(link.id)}
                          >
                            Withdraw link
                          </Button>
                        </>
                      ) : (
                        <Button
                          variant="quiet"
                          busy={busy === p.id}
                          aria-label={`New link for ${p.project_number}`}
                          disabled={p.project_status === 'archived'}
                          title={
                            p.project_status === 'archived'
                              ? 'An archived project has no new links: restore it first.'
                              : undefined
                          }
                          onClick={() => void makeLink(p)}
                        >
                          New link
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </TableWrap>
      )}
    </section>
  );
}
